/**
 * Geocoding 결과 집계 유틸리티
 */

import { MultiFullGeocodingReqBody } from "../data_provider/gateways/RequestGateway";

export interface GeocodingStats {
    totalCnt: number;
    exactCnt: number;
    similarCnt: number;
    failCnt: number;
    matchingRate: number;  // 매칭율 (%)
    // geocodingProcessingTime: number;  // 처리시간 (초)
	failedAddresses?: string[];
}

export interface GeocodingResultItem {
    index: string;
    data: {
        address: string | null;
        coords: number[] | null;
        matchStatus: string;
    } | null;
}

/**
 * Geocoding 결과에서 exact/similar/fail 건수를 집계
 * @param geocodingResults MultiFullGeocoding 응답 결과 배열
 * @returns GeocodingStats 집계 결과
 */
export function aggregateGeocodingStats(geocodingResults: GeocodingResultItem[]): GeocodingStats {
    let exactCnt = 0;
    let similarCnt = 0;
    let failCnt = 0;

    geocodingResults.forEach(result => {
        if (result.data === null) {
            // 데이터가 null인 경우 실패로 간주
            failCnt++;
        } else {
            switch (result.data.matchStatus) {
                case 'exact':
                    exactCnt++;
                    break;
                case 'similar':
                    similarCnt++;
                    break;
                default:
                    failCnt++;
                    break;
            }
        }
    });

    const matchingRate = geocodingResults.length > 0 ? Math.round((exactCnt / geocodingResults.length) * 100) : 0;

    return {
        totalCnt: geocodingResults.length,
        exactCnt,
        similarCnt,
        failCnt,
        matchingRate,
        // geocodingProcessingTime: 0  // 집계 함수에서는 0으로 초기화
    };
}

/**
 * 실패한 geocoding 결과만 필터링
 * @param geocodingResults MultiFullGeocoding 응답 결과 배열
 * @returns 실패한 결과 배열
 */
export function getFailedGeocodingResults(geocodingResults: GeocodingResultItem[]): GeocodingResultItem[] {
    return geocodingResults.filter(result =>
        result.data === null ||
        (result.data.matchStatus !== 'exact' && result.data.matchStatus !== 'similar')
    );
}

/**
 * 실패 결과(failedResults)의 index를 기준으로,
 * 요청 본문(body.addresses)에서 동일 index의 query(주소 문자열)를 찾아 반환합니다.
 *
 * @param failedResults 실패로 판정된 GeocodingResultItem 배열
 * @param body MultiFullGeocoding 요청 본문(주소 목록 포함)
 * @returns 실패한 주소(query) 문자열 배열
 */
export const getFailedQueriesFromFailedResults = (failedResults: GeocodingResultItem[], body: MultiFullGeocodingReqBody): string[] => {
	// 실패 index(number) 목록
	const failedIndexes = failedResults
		.map((r) => Number(r.index))
		.filter((n) => Number.isFinite(n));

	// body.addresses에서 index -> query 매핑
	const queryByIndex = new Map<number, string>();
	for (const a of body.addresses ?? []) {
		queryByIndex.set(a.index, a.query);
	}

	// 실패 index에 해당하는 query 추출
	return failedIndexes
		.map((idx) => queryByIndex.get(idx))
		.filter((q): q is string => Boolean(q));
};